<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { AuthService } from '../../../lib/api/auth.service';
  import EmployeeDonationService, { type ExtendedCharity, type DonationRequest } from '../../../lib/api/employee/donation.service';
  import { MyEmployerService, type Employer } from '../../../lib/api/employee/myEmployer.service';
  
  // Import new step components
  import InitialRequestStep from './donation-steps/InitialRequestStep.svelte';
  import CharitySelectionStep from './donation-steps/CharitySelectionStep.svelte';
  import InviteCharityStep from './donation-steps/InviteCharityStep.svelte';
  import ReviewStep from './donation-steps/ReviewStep.svelte';
  import SuccessStep from './donation-steps/SuccessStep.svelte';
  import Notification from '../../Notification.svelte';

  export let isOpen = false;
  export let preSelectedCharityUuid: string | null = null;

  const dispatch = createEventDispatcher();

  // Step management
  let currentStep = 0;
  const steps = ['initial', 'charity', 'invite', 'review', 'success'];

  // Form data
  let selectedEmployer = '';
  let leaveHours = '';
  let selectedCharity: ExtendedCharity | null = null;
  let charities: ExtendedCharity[] = [];
  let employerOptions: Array<{ uuid: string; name: string; status?: string }> = [];
  
  // Loading states
  let loading = false;
  let isLoadingCharities = false;
  let searchQuery = '';
  let searchTimeout: ReturnType<typeof setTimeout>;

  // User data
  let user: any = null;

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
  }



  // Step navigation functions
  function goToStep(stepName: string) {
    const stepIndex = steps.indexOf(stepName);
    if (stepIndex !== -1) {
      currentStep = stepIndex;
    }
  }

  function nextStep() {
    if (currentStep < steps.length - 1) {
      currentStep++;
    }
  }

  function previousStep() {
    if (currentStep > 0) {
      currentStep--;
    }
  }

  // Load initial data – only verified (approved/active) employers can be selected for a donation request
  async function loadEmployers() {
    if (!user) return;

    try {
      const result = await MyEmployerService.getEmployers(user.uuid);
      if (result.success && result.data) {
        employerOptions = result.data
          .filter((employer: Employer) => employer.status === 'approved' || employer.status === 'seeded')
          .map((employer: Employer) => ({
            uuid: employer.uuid,
            name: employer.name,
            status: employer.status
          }));
      }
    } catch (err) {
      console.error('Error loading employers:', err);
    }
  }

  async function loadCharities() {
    isLoadingCharities = true;
    try {
      const result = await EmployeeDonationService.getCharities({
        per_page: 25,
        verified: true
      });

      if (result.success && result.data) {
        charities = result.data.map(charity => ({
          ...charity,
          description: charity.description || `${charity.name} - Verified charity providing community support`,
          logo: charity.logo || undefined
        }));
        
        // Pre-select charity if preSelectedCharityUuid is provided
        if (preSelectedCharityUuid && !selectedCharity) {
          const preSelected = charities.find(c => c.uuid === preSelectedCharityUuid);
          if (preSelected) {
            selectedCharity = preSelected;
            // Don't skip steps - start at initial step
          }
        }
      } else {
        showNotification('Failed to load charities', 'error');
      }
    } catch (error) {
      console.error('Error loading charities:', error);
      showNotification('Failed to load charities. Please try again.', 'error');
      charities = [];
    } finally {
      isLoadingCharities = false;
    }
  }

  async function searchCharities(query: string) {
    if (!query.trim()) {
      await loadCharities();
      return;
    }

    isLoadingCharities = true;
    try {
      const result = await EmployeeDonationService.getCharities({
        phrase: query.trim(),
        per_page: 25,
        verified: true
      });

      if (result.success && result.data) {
        charities = result.data.map(charity => ({
          ...charity,
          description: charity.description || `${charity.name} - Verified charity providing community support`,
          logo: charity.logo || undefined
        }));
      } else {
        showNotification('Failed to search charities', 'error');
      }
    } catch (error) {
      console.error('Error searching charities:', error);
      showNotification('Failed to search charities. Please try again.', 'error');
      charities = [];
    } finally {
      isLoadingCharities = false;
    }
  }

  // Step event handlers
  function handleInitialNext() {
    nextStep();
  }

  async function handleRefreshEmployers() {
    await loadEmployers();
  }

  async function handleRefreshCharities() {
    await loadCharities();
  }

  function handleCharitySelected(event: CustomEvent) {
    selectedCharity = event.detail.charity;
  }

  function handleCharityNext() {
    nextStep();
  }

  function handleGoToReview() {
    goToStep('review');
  }

  function handleInviteCharity() {
    goToStep('invite');
  }

  async function handleInviteSubmit(event: CustomEvent) {
    loading = true;
    try {
      const invitationData = event.detail.data;
      const result = await EmployeeDonationService.inviteCharity(invitationData);
      if (result.success) {
        showNotification('Charity invitation sent successfully!', 'success');
        // Refresh charities list and go back to charity selection
        await loadCharities();
        goToStep('charity');
      } else {
        const errorMessage = result.errors.errors || 'Failed to send charity invitation';
        showNotification(errorMessage, 'error');
      }
    } catch (err: any) {
      console.error('Error inviting charity:', err);
      
      let errorMessage = 'An unexpected error occurred. Please try again.';
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      loading = false;
    }
  }

  function handleSearch(event: CustomEvent) {
    const query = event.detail.query;
    searchQuery = query;
    
    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    searchTimeout = setTimeout(() => {
      searchCharities(query);
    }, 300);
  }

  let currentDonation: DonationRequest | null = null;

  async function handleConfirmDonation() {
    if (!user || !selectedCharity || !selectedEmployer || !leaveHours) {
      showNotification('Missing required information. Please fill all fields.', 'error');
      return;
    }

    loading = true;
    try {
      const donationData = {
        charity_uuid: selectedCharity.uuid,
        leave_hours: parseInt(leaveHours),
        employer_uuid: selectedEmployer,
        user_uuid: user.uuid,
      };

      const result = await EmployeeDonationService.createDonationRequest(donationData);
      
      if (result.success && result.data) {
        currentDonation = result.data;
        showNotification('Donation request submitted successfully!', 'success');
        nextStep(); // Go to success step
      } else {
        // Handle Laravel API error response
        const errorMessage = result.message || 'Failed to create donation request';
        showNotification(errorMessage, 'error');
      }
    } catch (err: any) {
      console.error('Error creating donation:', err);
      
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      loading = false;
    }
  }

  function handleViewRequest() {
    if (!currentDonation) {
      showNotification('Request not available', 'error');
      return;
    }

    try {
      // Navigate to donation detail view
      push(`/my-donation/${currentDonation.uuid}`);
      closeModal();
    } catch (error) {
      console.error('Error viewing request:', error);
      showNotification('Failed to view request. Please try again.', 'error');
    }
  }

  function handleReturnToDashboard() {
    closeModal();
    dispatch('complete');
  }

  function handleBack() {
    if (currentStep === 2) { // invite step
      goToStep('charity'); // Go back to charity selection
    } else if (currentStep === 3) {
      goToStep('charity'); // Go back to charity selection
    } else {
      previousStep();
    }
  }

  function handleCancel() {
    closeModal();
  }

  function handleError(event: CustomEvent) {
    showNotification(event.detail.message, 'error');
  }

  function closeModal() {
    isOpen = false;
    currentStep = 0;
    selectedEmployer = '';
    leaveHours = '';
    selectedCharity = null;
    searchQuery = '';
    currentDonation = null;
    dispatch('close');
  }

  // Initialize data when modal opens
  $: if (isOpen && !user) {
    user = AuthService.getUser();
    if (user) {
      loadEmployers();
      loadCharities();
    }
  }

  // Handle pre-selected charity when modal opens
  $: if (isOpen && preSelectedCharityUuid && charities.length > 0 && !selectedCharity) {
    const preSelected = charities.find(c => c.uuid === preSelectedCharityUuid);
    if (preSelected) {
      selectedCharity = preSelected;
      // Don't skip steps - start at initial step and show charity as read-only
    }
  }
</script>

{#if isOpen}
  <div class="modal-overlay" role="presentation" on:click|self={closeModal} on:keydown={(e) => e.key === 'Escape' && closeModal()}>
    <div class="modal-container" role="dialog" aria-modal="true" aria-label="Donation request">
      <div class="modal-content">
        


        <!-- Step content -->
        {#if currentStep === 0}
          <InitialRequestStep
            bind:selectedEmployer
            bind:leaveHours
            {employerOptions}
            {loading}
            on:next={handleInitialNext}
            on:cancel={handleCancel}
            on:error={handleError}
            on:refresh-employers={handleRefreshEmployers}
            on:show-notification={(e) => showNotification(e.detail.message, e.detail.type)}
          />
        {:else if currentStep === 1}
          <CharitySelectionStep
            bind:selectedCharity
            {charities}
            bind:searchQuery
            {isLoadingCharities}
            {loading}
            isReadOnly={!!preSelectedCharityUuid}
            on:charitySelected={handleCharitySelected}
            on:next={handleCharityNext}
            on:back={handleBack}
            on:inviteCharity={handleInviteCharity}
            on:search={handleSearch}
            on:goToReview={handleGoToReview}
            on:refresh-charities={handleRefreshCharities}
            on:show-notification={(e) => showNotification(e.detail.message, e.detail.type)}
          />
        {:else if currentStep === 2}
          <InviteCharityStep
            {loading}
            on:submit={handleInviteSubmit}
            on:cancel={handleCancel}
            on:back={handleBack}
            on:error={handleError}
          />
        {:else if currentStep === 3}
          <ReviewStep
            {selectedCharity}
            {selectedEmployer}
            {employerOptions}
            {leaveHours}
            {loading}
            on:confirm={handleConfirmDonation}
            on:cancel={handleCancel}
            on:back={handleBack}
          />
        {:else if currentStep === 4}
          <SuccessStep
            charityName={selectedCharity?.name || 'Unknown Charity'}
            {leaveHours}
            on:viewRequest={handleViewRequest}
            on:returnToDashboard={handleReturnToDashboard}
          />
        {/if}
      </div>
    </div>
  </div>
{/if}

<!-- Notification Component -->
<Notification 
  show={notificationShow} 
  type={notificationType} 
  message={notificationMessage} 
  onDismiss={() => { notificationShow = false; }}
/>

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
  }

  .modal-container {
    background: white;
    border-radius: 12px;
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }

  .modal-content {
    position: relative;
  }



  /* Responsive design */
  @media (max-width: 768px) {
    .modal-overlay {
      padding: 0.5rem;
    }

    .modal-container {
      max-height: 95vh;
    }
  }
</style> 